import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  buildPath,
  buildTemplatePath,
  pn,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
} from "@compass/core";

import { ListFooter, ReorderDataModels } from "components";
import { EndReasonListDto, EndReasonListResource, ListFilterBase, Permission, DataModelEnum, PermissionAction } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { isAuthorized } from "services";
import { EndReasonListPageHeader } from "./EndReasonListPageHeader";
import { EndReasonTable } from "./EndReasonTable";

const routeName = "endReasons";

export const endReasonListPageRoute: PageRoute = {
  handle: "End Reasons",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const EndReasonListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<EndReasonListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
    sortObjects: [{ property: pn<EndReasonListDto>("name"), isDesc: false }],
  });
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<EndReasonListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.endReasons.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.endReasons.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);

    ApiService.endReasons.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  return (
    <>
      <Page
        header={<EndReasonListPageHeader rootPermission={ROOT_PERMISSION} handle={endReasonListPageRoute.handle} />}
      >
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <EndReasonTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              endReasons={listItems}
              setEndReasons={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
              reorderDataModels={
                  isAuthorized(ROOT_PERMISSION, PermissionAction.Update) ? (
                    <ReorderDataModels
                      dataModelEnum={DataModelEnum.EndReason}
                      handle={endReasonListPageRoute.handle}
                      getId={(item) => item.id}
                      getName={(item) => item.name}
                      getDisplayOrder={(item) => item.displayOrder}
                      fetch={() =>
                        ApiService.endReasons.getList({
                          page: PAGE_DEFAULT,
                          pageSize: PAGE_SIZE_UNLIMITED,
                        })
                      }
                    />
                  ) : undefined
                }
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
